import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { PAYROLL_RUN_STEP, type PayrollRunStep } from "./payroll-run-page";

export const IGNITION_MS = 2400;
export const SETTLE_MS = 700;

export const getPayrollRunStep = ({
  showNoRunYet,
  showInitialCalculationState,
  isStartingRun,
  hasActiveRun,
  activeRun,
}: {
  showNoRunYet: boolean;
  showInitialCalculationState: boolean;
  isStartingRun: boolean;
  hasActiveRun: boolean;
  activeRun: { isQueued: boolean } | null;
}): PayrollRunStep | null => {
  if (showInitialCalculationState)
    return activeRun?.isQueued === false
      ? PAYROLL_RUN_STEP.running
      : PAYROLL_RUN_STEP.starting;
  if (showNoRunYet)
    return isStartingRun || hasActiveRun
      ? PAYROLL_RUN_STEP.starting
      : PAYROLL_RUN_STEP.notRun;
  return null;
};

export const usePayrollRunReveal = (
  liveStep: PayrollRunStep | null,
  hasResults: boolean,
) => {
  const [previous, setPrevious] = useState(liveStep);
  const [holding, setHolding] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const firingSince = useRef<number | null>(null);

  if (previous !== liveStep) {
    setPrevious(liveStep);
    const runEnded =
      previous === PAYROLL_RUN_STEP.starting ||
      previous === PAYROLL_RUN_STEP.running;
    if (liveStep === null && hasResults && runEnded) setHolding(true);
    if (liveStep !== null) setRevealed(false);
  }

  useEffect(() => {
    if (liveStep === PAYROLL_RUN_STEP.running)
      firingSince.current ??= dayjs().valueOf();
    if (liveStep === PAYROLL_RUN_STEP.notRun) firingSince.current = null;
  }, [liveStep]);

  useEffect(() => {
    if (!holding) return;
    const fired =
      firingSince.current === null
        ? 0
        : dayjs().valueOf() - firingSince.current;
    const timer = setTimeout(
      () => {
        firingSince.current = null;
        setHolding(false);
        setRevealed(true);
      },
      fired >= IGNITION_MS ? SETTLE_MS : IGNITION_MS,
    );
    return () => clearTimeout(timer);
  }, [holding]);

  return {
    step: holding ? PAYROLL_RUN_STEP.finishing : liveStep,
    holding,
    revealed,
  };
};
